// Pre-built Open Casting Call templates for the campaign composer.
// Voice: written by the founder — "for us, to us, from us."
// Every email offers two CTAs:
//   1. Primary → /original-series (landing page with the trailer + full context)
//   2. Secondary → /apply (the actual casting form)
// A trailer thumbnail with a gold play overlay sits above the primary CTA to
// give recipients a visual hook before they click through — email clients that
// support images typically see 30-40% higher CTR when the still image is present.
// Three tones mirror the draft-nudge tones so the whole casting funnel feels
// like one voice: warm (soft first call) · urgent (filming close) · deadline (last call).

const originUrl = () => `${window.location.origin.replace(/\/$/, "")}`;
const trailerUrl = () => `${originUrl()}/original-series`;
const applyUrl = () => `${originUrl()}/apply`;

// Series hero still — same image used on the Original Series landing page.
const TRAILER_THUMB = "https://images.unsplash.com/photo-1758738180478-0fd260c86605?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxibGFjayUyMHdvbWFuJTIwcG9ydHJhaXQlMjBlbGVnYW50JTIwZ29sZHxlbnwwfHx8fDE3ODU2ODY4ODl8MA&ixlib=rb-4.1.0&q=85&w=600";

// Table-based thumbnail with a centered play button — the safest cross-client pattern
// (works in Gmail, Outlook, Apple Mail, iOS Mail). The whole block is a link.
const thumbBlock = (href) => `
  <a href="${href}" style="display:block;text-decoration:none;color:inherit;margin:0 0 28px 0">
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="border-collapse:collapse">
      <tr>
        <td style="background:#0b0b0b;padding:0;position:relative">
          <img src="${TRAILER_THUMB}" alt="Watch the Mrs.Black(In)Love trailer" width="600" style="display:block;width:100%;max-width:600px;height:auto;border:0;outline:none;text-decoration:none;filter:brightness(0.75)" />
        </td>
      </tr>
      <tr>
        <td style="background:#0b0b0b;padding:0 0 14px 0;text-align:center">
          <table role="presentation" cellspacing="0" cellpadding="0" border="0" align="center" style="margin:-40px auto 0 auto;position:relative">
            <tr>
              <td style="background:#c9a24a;width:56px;height:56px;border-radius:50%;text-align:center;vertical-align:middle;line-height:56px;color:#0b0b0b;font-size:24px;font-family:Arial,Helvetica,sans-serif">&#9654;</td>
            </tr>
          </table>
          <div style="font-family:Helvetica,Arial,sans-serif;color:#c9a24a;font-size:11px;letter-spacing:0.28em;text-transform:uppercase;margin-top:12px">Watch the trailer · 90 sec</div>
        </td>
      </tr>
    </table>
  </a>`;

const shell = ({ eyebrow, headline, lede, closer, preheader }) => `
<div style="display:none!important;visibility:hidden;opacity:0;color:transparent;height:0;width:0;font-size:1px;line-height:1px;max-height:0;max-width:0;overflow:hidden;mso-hide:all">${preheader || "Watch the 90-sec trailer for our first original series →"}${"&#847;&nbsp;".repeat(80)}</div>
<div style="font-family:Helvetica,Arial,sans-serif;background:#0b0b0b;color:#f5efe6;padding:40px 24px;max-width:600px;margin:auto">
  <p style="font-size:0.7rem;letter-spacing:0.28em;text-transform:uppercase;color:#c9a24a;margin:0 0 16px 0">${eyebrow}</p>
  <h1 style="font-family:'Cormorant Garamond',Georgia,serif;font-size:36px;line-height:1.05;margin:0 0 20px 0;color:#f5efe6">${headline}</h1>
  <p style="color:#c9c1b3;font-size:16px;line-height:1.6">Hi {{first_name}},</p>
  <p style="color:#c9c1b3;font-size:16px;line-height:1.6">${lede}</p>
  ${thumbBlock(trailerUrl())}
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin:0 0 24px 0">
    <tr>
      <td style="padding-right:12px">
        <a href="${trailerUrl()}" style="display:inline-block;background:#c9a24a;color:#0b0b0b;text-decoration:none;padding:14px 22px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;font-size:12px">Watch the trailer →</a>
      </td>
      <td>
        <a href="${applyUrl()}" style="display:inline-block;background:transparent;color:#c9a24a;border:1px solid #c9a24a;text-decoration:none;padding:13px 22px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;font-size:12px">Apply now →</a>
      </td>
    </tr>
  </table>
  <p style="color:#c9c1b3;font-size:16px;line-height:1.6">${closer}</p>
  <p style="color:#8b8478;font-size:12px;line-height:1.6;margin-top:32px">Buttons not working? Copy these into your browser:<br/><span style="word-break:break-all;color:#c9c1b3">${trailerUrl()}</span><br/><span style="word-break:break-all;color:#c9c1b3">${applyUrl()}</span></p>
</div>`.trim();

const lede = "Mrs. Black(In)Love is now casting for an original unscripted series — accurate representation of us, to us, for us, from us. If you've ever been interested in showcasing your story or your life, watch the trailer for the full vision, then head over to the application whenever you're ready.";

export const castingCallTemplates = () => ([
    {
        key: "warm",
        label: "Warm — soft first call",
        description: "Gentle, no-pressure open casting invitation. Best as your first blast to the full contact list.",
        template: {
            name: "Open Casting Call — Warm",
            subject: "{{first_name}}, we're casting for an original series",
            body_html: shell({
                eyebrow: "Mrs.Black(In)Love · Casting",
                headline: "Casting is open.",
                lede,
                closer: "Whenever the moment feels right — we'd love to see you.",
                preheader: "Watch the 90-sec trailer for our first original series →",
            }),
        },
    },
    {
        key: "urgent",
        label: "Urgent — filming weekend close",
        description: "Momentum push. Use once the casting window is halfway through and you want to encourage sitting-on-the-fence folks to submit.",
        template: {
            name: "Open Casting Call — Urgent",
            subject: "{{first_name}}, a quick nudge from casting",
            body_html: shell({
                eyebrow: "Mrs.Black(In)Love · Casting",
                headline: "A quick nudge from casting.",
                lede: `${lede} Applications for our upcoming filming weekend are moving fast — take five minutes today so you don't miss the window.`,
                closer: "We're rooting for you.",
                preheader: "Filming weekend is close — watch the 90-sec trailer inside →",
            }),
        },
    },
    {
        key: "deadline",
        label: "Deadline — last call",
        description: "Final blast in the last 48–72 hours before casting closes. Direct, warm, deadline-driven.",
        template: {
            name: "Open Casting Call — Deadline",
            subject: "Casting closes soon — last call, {{first_name}}",
            body_html: shell({
                eyebrow: "Mrs.Black(In)Love · Casting",
                headline: "Casting closes soon.",
                lede: `${lede} This is the last stretch before our casting window closes — if this series is calling you, now is the moment.`,
                closer: "Don't let the window close on you.",
                preheader: "Last call — the trailer, the story, the application →",
            }),
        },
    },
]);
